/**
 * KYC Compliance page — Finance Manager / individual user view (kyc-1-1 AC:10)
 *
 * Shows required documents checklist with status badges, upload buttons,
 * rejection reasons, and expiry warnings.
 *
 * Route: /settings/kyc
 */

import React, { useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  Shield,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  Loader2,
  AlertCircle,
  Info,
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

interface KycDocument {
  id: number;
  entity_type: 'org' | 'user';
  entity_id: number;
  doc_type: string;
  filename: string;
  file_size_bytes: number;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'expired';
  reviewed_by: number | null;
  reviewed_at: string | null;
  expiry_date: string | null;
  rejection_reason: string | null;
  uploaded_by: number;
  created_at: string;
}

// Required doc types for individual users
const USER_REQUIRED_DOCS = ['director_id', 'proof_address', 'pep_declaration'] as const;

const DOC_LABELS: Record<string, { label: string; description: string }> = {
  director_id:       { label: 'Identity Document', description: 'South African ID document or passport' },
  proof_address:     { label: 'Proof of Address', description: 'Bank statement or utility bill (max 3 months old)' },
  pep_declaration:   { label: 'PEP Declaration', description: 'Politically Exposed Person declaration form' },
  company_reg:       { label: 'Company Registration', description: 'CIPC company registration certificate' },
  fsp_licence:       { label: 'FSP Licence', description: 'Financial Services Provider licence (if applicable)' },
  beneficial_owner:  { label: 'Beneficial Owner', description: 'Declaration of beneficial ownership' },
  bank_confirm:      { label: 'Bank Confirmation', description: 'Bank account confirmation letter' },
  signatory_mandate: { label: 'Signatory Mandate', description: 'Authorised signatory mandate document' },
};

function formatDocType(dt: string): string {
  return DOC_LABELS[dt]?.label ?? dt.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function isExpiringSoon(expiryDate: string | null): boolean {
  if (!expiryDate) return false;
  const expiry = new Date(expiryDate);
  const daysLeft = Math.floor((expiry.getTime() - Date.now()) / 86400000);
  return daysLeft >= 0 && daysLeft < 30;
}

function daysUntilExpiry(expiryDate: string): number {
  return Math.floor((new Date(expiryDate).getTime() - Date.now()) / 86400000);
}

async function fetchMyDocs(token: string): Promise<KycDocument[]> {
  const res = await fetch(`${API_URL}/api/v1/kyc/documents`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch KYC documents');
  return res.json();
}

async function uploadDoc(
  token: string,
  entityType: string,
  entityId: number,
  docType: string,
  file: File,
): Promise<{ document_id: number; status: string }> {
  const form = new FormData();
  form.append('entity_type', entityType);
  form.append('entity_id', String(entityId));
  form.append('doc_type', docType);
  form.append('file', file);

  const res = await fetch(`${API_URL}/api/v1/kyc/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || 'Upload failed');
  }
  return res.json();
}

interface DocRowProps {
  docType: string;
  existingDoc: KycDocument | undefined;
  entityType: string;
  entityId: number;
  token: string;
  onUploaded: () => void;
}

const DocRow: React.FC<DocRowProps> = ({ docType, existingDoc, entityType, entityId, token, onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const label = formatDocType(docType);
  const desc = DOC_LABELS[docType]?.description ?? '';

  const status = existingDoc?.status;
  const expiringSoon = existingDoc ? isExpiringSoon(existingDoc.expiry_date) : false;

  const statusIcon = () => {
    if (!existingDoc) return <Clock className="h-5 w-5 text-gray-400" />;
    switch (existingDoc.status) {
      case 'approved':     return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'rejected':     return <XCircle className="h-5 w-5 text-red-500" />;
      case 'expired':      return <AlertTriangle className="h-5 w-5 text-orange-500" />;
      case 'pending':
      case 'under_review': return <Clock className="h-5 w-5 text-yellow-500" />;
      default:             return <Clock className="h-5 w-5 text-gray-400" />;
    }
  };

  const badgeClass = () => {
    if (!existingDoc) return 'bg-gray-100 text-gray-500';
    switch (existingDoc.status) {
      case 'approved':     return 'bg-green-100 text-green-700';
      case 'rejected':     return 'bg-red-100 text-red-700';
      case 'expired':      return 'bg-orange-100 text-orange-700';
      case 'pending':      return 'bg-yellow-100 text-yellow-700';
      case 'under_review': return 'bg-blue-100 text-blue-700';
      default:             return 'bg-gray-100 text-gray-500';
    }
  };

  const badgeLabel = () => {
    if (!existingDoc) return 'Not uploaded';
    return existingDoc.status.replace('_', ' ').replace(/^\w/, c => c.toUpperCase());
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    setUploading(true);
    try {
      await uploadDoc(token, entityType, entityId, docType, file);
      onUploaded();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setUploadError(msg);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const canUpload = !existingDoc || ['rejected', 'expired'].includes(existingDoc.status ?? '');

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5">{statusIcon()}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900">{label}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badgeClass()}`}>
                {badgeLabel()}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{desc}</p>

            {existingDoc?.filename && (
              <p className="text-xs text-gray-400 mt-0.5">
                <FileText className="h-3 w-3 inline mr-1" />{existingDoc.filename}
              </p>
            )}

            {existingDoc?.rejection_reason && (
              <div className="mt-2 text-xs bg-red-50 border border-red-100 rounded p-2 text-red-700">
                <strong>Rejected:</strong> {existingDoc.rejection_reason}
              </div>
            )}

            {existingDoc?.expiry_date && (
              <div className={`mt-1 text-xs ${expiringSoon ? 'text-orange-600 font-medium' : 'text-gray-500'}`}>
                {expiringSoon ? (
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Expires in {daysUntilExpiry(existingDoc.expiry_date)} days ({existingDoc.expiry_date})
                  </span>
                ) : (
                  `Expires: ${existingDoc.expiry_date}`
                )}
              </div>
            )}

            {uploadError && (
              <p className="text-xs text-red-600 mt-1">{uploadError}</p>
            )}
          </div>
        </div>

        {canUpload && (
          <div>
            <input
              ref={fileRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="hidden"
              id={`upload-${docType}`}
            />
            <label
              htmlFor={`upload-${docType}`}
              className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                uploading
                  ? 'bg-gray-100 text-gray-400 pointer-events-none'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {uploading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Upload className="h-3.5 w-3.5" />
              )}
              {uploading ? 'Uploading…' : existingDoc ? 'Re-upload' : 'Upload'}
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

const KycCompliance: React.FC = () => {
  const { user } = useAuth();
  const userId = user?.id ?? 0;
  const token = localStorage.getItem('access_token') || '';
  const queryClient = useQueryClient();

  const { data: docs, isLoading, error } = useQuery({
    queryKey: ['kyc-documents', userId],
    queryFn: () => fetchMyDocs(token),
  });

  const handleUploaded = () => {
    queryClient.invalidateQueries({ queryKey: ['kyc-documents', userId] });
  };

  // Compute overall KYC status
  const computeStatus = (): 'incomplete' | 'pending_review' | 'approved' => {
    if (!docs) return 'incomplete';
    const uploaded = new Set(
      docs.filter(d => d.entity_type === 'user' && d.entity_id === userId && d.status !== 'expired')
          .map(d => d.doc_type)
    );
    const approved = new Set(
      docs.filter(d => d.entity_type === 'user' && d.entity_id === userId && d.status === 'approved')
          .map(d => d.doc_type)
    );
    const required = new Set(USER_REQUIRED_DOCS);
    if ([...required].every(r => approved.has(r))) return 'approved';
    if ([...required].every(r => uploaded.has(r))) return 'pending_review';
    return 'incomplete';
  };

  const overallStatus = computeStatus();

  const statusBannerClass = {
    incomplete:     'bg-yellow-50 border-yellow-200 text-yellow-800',
    pending_review: 'bg-blue-50 border-blue-200 text-blue-800',
    approved:       'bg-green-50 border-green-200 text-green-800',
  }[overallStatus];

  const statusBannerMsg = {
    incomplete:     'Some required documents are missing. Please upload all required documents to enable broker access.',
    pending_review: 'All required documents submitted. Our compliance team will review them shortly.',
    approved:       'KYC verification complete. Broker access is enabled.',
  }[overallStatus];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-48">
        <Loader2 className="animate-spin h-7 w-7 text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-red-500 flex items-center gap-2">
        <AlertCircle className="h-5 w-5" /> Failed to load KYC documents
      </div>
    );
  }

  const userDocs = docs?.filter(d => d.entity_type === 'user' && d.entity_id === userId) ?? [];
  const docByType = Object.fromEntries(userDocs.map(d => [d.doc_type, d]));

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <Shield className="h-6 w-6 text-blue-600" />
        <div>
          <h2 className="text-xl font-bold text-gray-900">Identity Verification (KYC)</h2>
          <p className="text-sm text-gray-500">
            Required by FICA (Financial Intelligence Centre Act) before broker access
          </p>
        </div>
      </div>

      {/* Overall status banner */}
      <div className={`border rounded-xl p-4 flex items-start gap-3 ${statusBannerClass}`}>
        {overallStatus === 'approved' ? (
          <CheckCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
        ) : overallStatus === 'pending_review' ? (
          <Clock className="h-5 w-5 flex-shrink-0 mt-0.5" />
        ) : (
          <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
        )}
        <p className="text-sm">{statusBannerMsg}</p>
      </div>

      {/* Required documents */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3">Required Documents</h3>
        <div className="space-y-3">
          {USER_REQUIRED_DOCS.map(dt => (
            <DocRow
              key={dt}
              docType={dt}
              existingDoc={docByType[dt]}
              entityType="user"
              entityId={userId}
              token={token}
              onUploaded={handleUploaded}
            />
          ))}
        </div>
      </div>

      {/* Disclosure */}
      <div className="text-xs text-gray-400 bg-gray-50 border border-gray-100 rounded-xl p-4">
        <p className="font-medium text-gray-500 mb-1">FICA Compliance Notice</p>
        <p>
          my investments (operated by Marome Investments (Pty) Ltd) is required by the Financial
          Intelligence Centre Act (FICA) to verify the identity of all clients before providing
          broker-linked services. Documents are stored securely and are not shared with third parties
          except as required by law. Records are retained for a minimum of 5 years as required by FICA.
        </p>
      </div>
    </div>
  );
};

export default KycCompliance;
